import React from 'react';
import { BookOpen, Users, Palette, Code } from 'lucide-react';
import { authService } from '../services/auth';
import { AUTH_CONFIG } from '../config/auth';
import Logo from './Logo';

const SplashScreen: React.FC = () => {
  const [error, setError] = React.useState<string | null>(null);

  const handleSignIn = async () => {
    setError(null);
    try {
      await authService.signInWithGoogle();
    } catch (error) {
      console.error('Sign in failed:', error);
      setError(error instanceof Error ? error.message : 'Sign in failed. Please try again.');
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 via-blue-50 to-indigo-100 dark:from-slate-900 dark:via-slate-800 dark:to-slate-900 flex items-center justify-center">
      {/* Animated background elements */}
      <div className="fixed inset-0 overflow-hidden pointer-events-none">
        <div className="absolute -top-40 -right-40 w-80 h-80 bg-gradient-to-br from-blue-400/20 to-indigo-500/20 rounded-full blur-3xl floating"></div>
        <div className="absolute -bottom-40 -left-40 w-80 h-80 bg-gradient-to-br from-indigo-400/20 to-purple-500/20 rounded-full blur-3xl floating" style={{ animationDelay: '2s' }}></div>
        <div className="absolute top-1/2 left-1/2 transform -translate-x-1/2 -translate-y-1/2 w-96 h-96 bg-gradient-to-br from-blue-300/10 to-indigo-400/10 rounded-full blur-3xl floating" style={{ animationDelay: '4s' }}></div>
      </div>

      <div className="relative z-10 max-w-4xl mx-auto px-6 py-12">
        <div className="text-center mb-12">
          {/* Logo and Title */}
          <div className="flex items-center justify-center mb-8">
            <Logo className="h-16 w-auto mr-4" />
            <div className="text-left">
              <h1 className="text-4xl font-bold gradient-text">PatternLab</h1>
              <p className="text-lg text-slate-600 dark:text-slate-300">Dashboard</p>
            </div>
          </div>

          {/* Main Heading */}
          <h2 className="text-3xl font-bold text-slate-900 dark:text-white mb-4">
            Welcome to PatternLab Dashboard
          </h2>
          <p className="text-xl text-slate-600 dark:text-slate-300 mb-12 max-w-2xl mx-auto">
          Discover, organize, and track patterns across all our Pattern Lab projects.<br></br>
          Sign in to explore your modules and bookmarks.
          </p>

          {/* Features Grid */}
          <div className="grid md:grid-cols-3 gap-8 mb-12">
            <div className="card liquid-glass p-6">
              <div className="w-12 h-12 bg-blue-100 dark:bg-blue-900/30 rounded-lg flex items-center justify-center mb-4 mx-auto">
                <Palette className="w-6 h-6 text-blue-600 dark:text-blue-400" />
              </div>
              <h3 className="text-lg font-semibold text-slate-900 dark:text-white mb-2">Pattern Labs</h3>
              <p className="text-slate-600 dark:text-slate-300">
                Explore libraries of patterns and modules from all repositories - fully indexed and easy to browse.
              </p>
            </div>

            <div className="card liquid-glass p-6">
              <div className="w-12 h-12 bg-green-100 dark:bg-green-900/30 rounded-lg flex items-center justify-center mb-4 mx-auto">
                <BookOpen className="w-6 h-6 text-green-600 dark:text-green-400" />
              </div>
              <h3 className="text-lg font-semibold text-slate-900 dark:text-white mb-2">Bookmarks</h3>
              <p className="text-slate-600 dark:text-slate-300">
                Bookmark your most-used patterns in Google Drive for faster access.
              </p>
            </div>

            <div className="card liquid-glass p-6">
              <div className="w-12 h-12 bg-yellow-100 dark:bg-yellow-900/30 rounded-lg flex items-center justify-center mb-4 mx-auto">
                <Code className="w-6 h-6 text-yellow-600 dark:text-yellow-400" />
              </div>
              <h3 className="text-lg font-semibold text-slate-900 dark:text-white mb-2">Pattern Insights</h3>
              <p className="text-slate-600 dark:text-slate-300">
                All details combined: context, usage notes, previews, and resource links in one view.
              </p>
            </div>
          </div>

          {/* Sign In Button */}
          <div className="mb-8">
            <button
              onClick={handleSignIn}
              className="btn-primary px-8 py-4 text-lg font-medium flex items-center justify-center mx-auto"
            >
              <svg className="w-6 h-6 mr-3" viewBox="0 0 24 24">
                <path fill="currentColor" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"/>
                <path fill="currentColor" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"/>
                <path fill="currentColor" d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"/>
                <path fill="currentColor" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"/>
              </svg>
              <span>Sign in with Google</span>
            </button>
          </div>

          {/* Error Message */}
          {error && (
            <div className="mb-6 p-4 bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg">
              <p className="text-red-600 dark:text-red-400 text-sm">{error}</p>
            </div>
          )}

          {/* Footer */}
          <div className="text-slate-500 dark:text-slate-400 text-sm">
            <p>By signing in, you agree to our terms of service and privacy policy.</p>
            <p className="mt-2">
              <strong>Access Restricted:</strong> Only users from{' '}
              <span className="font-semibold text-slate-700 dark:text-slate-300">
                {AUTH_CONFIG.ALLOWED_EMAIL_DOMAINS.join(', ')}
              </span>{' '}
              can sign in.
            </p>
          </div>
        </div>
      </div>
    </div>
  );
};

export default SplashScreen;
